import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { PowerLimitationHistoryApi } from '@vi-iot/client/apis/power-limitation-history/power-limitation-history.api';
import { PowerLimitationHistoryPayload } from '@vi-iot/client/apis/power-limitation-history/power-limitation-history.model';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PowerLimitationHistoryActions } from './power-limitation-history.actions';

import { DateTimeService } from '@services';
import { ViError } from '@vi-iot/client/models';

import { DevicesStore } from '../devices';
import { Gateway, InstallationDetailsStore } from '../installation-details';
import { PowerLimitationHistoryHelper } from './power-limitation-history.helper';
import { FilterType, PowerLimitationTableList } from './power-limitation-history.model';
import { PowerLimitationHistoryStore } from './power-limitation-history.store';
import dayjs from 'dayjs';

@Injectable()
export class PowerLimitationHistoryEffects {
  constructor(
    private actions$: Actions,
    private powerLimitationHistoryApi: PowerLimitationHistoryApi,
    private powerLimitationHistoryStore: PowerLimitationHistoryStore,
    private installationDetailsStore: InstallationDetailsStore,
    private devicesStore: DevicesStore,
    private dateTimeService: DateTimeService
  ) {}

  loadFilteredData$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(PowerLimitationHistoryActions.getPowerLimitationData.attempt),
      concatLatestFrom(() => [
        this.powerLimitationHistoryStore.filterDateAndType$,
        this.installationDetailsStore.getSelectedGateway$,
        this.devicesStore.getDevicesOfSelectedGateway$,
      ]),
      switchMap(([_action, filterDateAndType, gatewayProperties, devicesOfSelectedGateway]) => {
        const payload = this.createPowerLimitationPayload(
          filterDateAndType.date.toISOString(),
          filterDateAndType.type,
          gatewayProperties
        );

        return this.powerLimitationHistoryApi.getPowerLimitation(payload).pipe(
          map((powerLimitation) => {
            const format = this.dateTimeService.getFormat();
            const powerLimitationData: PowerLimitationTableList[] = PowerLimitationHistoryHelper.mapPowerLimitationResponse(
              powerLimitation.data.data,
              devicesOfSelectedGateway,
              powerLimitation.data.device_id,
              format
            );

            return PowerLimitationHistoryActions.getPowerLimitationData.success({
              powerLimitationData: powerLimitationData,
            });
          }),
          catchError((error: ViError) => {
            return of(PowerLimitationHistoryActions.getPowerLimitationData.error({ error }));
          })
        );
      })
    );
  });

  triggerLoadDataOnFilterDateAndTypeChange$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(PowerLimitationHistoryActions.updateFiltersByDate.attempt),
      map(() => PowerLimitationHistoryActions.getPowerLimitationData.attempt())
    );
  });

  private createPowerLimitationPayload(
    filterDateInput: string,
    filterDateType: FilterType,
    gatewayProperties: Gateway
  ): PowerLimitationHistoryPayload {
    let payload: PowerLimitationHistoryPayload;
    let startDate: string;
    let endDate: string;

    const nonTimeZoneFormat = 'YYYY-MM-DDTHH:mm:ss.SSS';
    const gatewayId = gatewayProperties.serial;
    const registeredAt = dayjs(gatewayProperties.registeredAt).format(nonTimeZoneFormat);
    const filterDate = dayjs(filterDateInput);

    switch (filterDateType) {
      case FilterType.Day:
        startDate = filterDate.startOf('day').format(nonTimeZoneFormat);
        endDate = filterDate.endOf('day').format(nonTimeZoneFormat);
        break;
      case FilterType.Month:
        startDate = filterDate.startOf('month').format(nonTimeZoneFormat);
        endDate = filterDate.endOf('month').format(nonTimeZoneFormat);
        break;
      case FilterType.Year:
        startDate = filterDate.startOf('year').format(nonTimeZoneFormat);
        endDate = filterDate.endOf('year').format(nonTimeZoneFormat);
        break;
      case FilterType.Total:
        startDate = registeredAt;
        endDate = dayjs().format(nonTimeZoneFormat);
        break;
    }

    payload = {
      start_datetime: startDate || registeredAt,
      end_datetime: endDate,
      gateway_id: gatewayId,
    };

    return payload;
  }
}
